import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SubHeader from "components/common/SubHeader";
import ProfileAvatar from "components/common/ProfileAvatar";
import PushSettingToggle from "components/mypage/setting/PushSettingToggle";
import CommonModal from "components/common/CommonModal";
import useUserStore from "@store/useUserStore";
import { useSnackbar } from "contexts/SnackbarContext";
import { getNotificationSettings, updateNotificationSettings } from "api/user";
import { TokenManager, notifyNativeLogout } from "utils/api";
import "assets/scss/pages/settings.scss";

const SettingsMenuItem = ({ title, onClick }) => (
    <div className="settings-menu-item" onClick={onClick}>
        <span>{title}</span>
        <span className="settings-menu-arrow">&rsaquo;</span>
    </div>
);

const Settings = () => {
    const navigate = useNavigate();
    const userInfo = useUserStore((state) => state.userInfo);
    const clearUserInfo = useUserStore((state) => state.clearUserInfo);
    const { showError } = useSnackbar();

    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [enableReservationPush, setEnableReservationPush] = useState(false);
    const [enableCommunityPush, setEnableCommunityPush] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [adminMenuList, setAdminMenuList] = useState([]);

    useEffect(() => {
        getNotificationSettings()
            .then((res) => {
                setEnableReservationPush(res.enable_activity_push ?? false);
                setEnableCommunityPush(res.enable_community_push ?? false);
            })
            .catch(() => {
                showError("알림 설정을 불러오지 못했습니다");
            })
            .finally(() => {
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (userInfo === null) return;
        if (userInfo.is_admin) {
        }
    }, [userInfo]);

    const handleReservationToggle = async (e) => {
        const newValue = e.target.checked;
        const prevValue = enableReservationPush;
        setEnableReservationPush(newValue);
        try {
            await updateNotificationSettings({ enable_activity_push: newValue });
        } catch {
            setEnableReservationPush(prevValue);
            showError("알림 설정 변경에 실패했습니다");
        }
    };

    const handleCommunityToggle = async (e) => {
        const newValue = e.target.checked;
        const prevValue = enableCommunityPush;
        setEnableCommunityPush(newValue);
        try {
            await updateNotificationSettings({ enable_community_push: newValue });
        } catch {
            setEnableCommunityPush(prevValue);
            showError("알림 설정 변경에 실패했습니다");
        }
    };

    const handleMenuClick = (link) => {
        if (link.includes("http")) {
            window.open(link, "_blank");
        } else {
            navigate(link);
        }
    };

    const onClickLogout = async () => {
        notifyNativeLogout();
        await TokenManager.clearTokens();
        localStorage.removeItem("userInfoStorage");
        clearUserInfo();
        navigate("/");
    };

    return (
        <div className="page-settings">
            <SubHeader title="설정"/>

            <div className="settings-content">
                {/* 프로필 */}
                {userInfo && (
                    <div className="settings-profile">
                        <ProfileAvatar
                            imageUrl={userInfo.profile_image_url}
                            size={56}
                        />
                        <div className="settings-profile-info">
                            <p className="settings-profile-name">{userInfo.nickname}</p>
                        </div>
                        <span
                            className="settings-profile-edit"
                            onClick={() => navigate("/mypage/user")}
                        >
                            편집 <svg
                                width="6"
                                height="10"
                                viewBox="0 0 6 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <path
                                    d="M1 1L5 5L1 9"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </div>
                )}

                {/* 알림 설정 */}
                <div className="settings-section">
                    <h3 className="settings-section-title">🔔 알림 설정</h3>
                    <div className="settings-notification-group">
                        <PushSettingToggle
                            label="예약 알림"
                            checked={enableReservationPush}
                            onChange={handleReservationToggle}
                            disabled={isLoading}
                        />
                        <p className="settings-toggle-desc">수업 예약, 주문/배송, 이벤트</p>
                    </div>
                    <div className="settings-notification-group">
                        <PushSettingToggle
                            label="커뮤니티 알림"
                            checked={enableCommunityPush}
                            onChange={handleCommunityToggle}
                            disabled={isLoading}
                        />
                        <p className="settings-toggle-desc">좋아요, 댓글, 멘션</p>
                    </div>
                </div>

                {/* 고객센터 */}
                <div className="settings-section">
                    <h3 className="settings-section-title">🎧 고객센터</h3>
                    <SettingsMenuItem title="1:1 카카오톡 문의" onClick={() => handleMenuClick("https://pf.kakao.com/_kZHxgn/chat")} />
                    <SettingsMenuItem title="더 딥스 카페" onClick={() => handleMenuClick("https://cafe.naver.com/freefenbeat.cafe")} />
                </div>

                {/* 관리자 */}
                {userInfo !== null && adminMenuList.length > 0 && (
                    <div className="settings-section">
                        <h3 className="settings-section-title">🔧 관리자</h3>
                        {adminMenuList.map((item, idx) => (
                            <SettingsMenuItem
                                key={`admin-${idx}`}
                                title={item.title}
                                onClick={() => navigate(item.link)}
                            />
                        ))}
                    </div>
                )}

                {/* 약관 */}
                <div className="settings-section">
                    <h3 className="settings-section-title">📄 약관 및 정책</h3>
                    <SettingsMenuItem title="서비스 이용약관" onClick={() => navigate("/policy/terms")} />
                    <SettingsMenuItem title="개인정보 처리방침" onClick={() => navigate("/policy/privacy")} />
                </div>

                {/* 앱 정보 */}
                <div className="settings-section">
                    <h3 className="settings-section-title">📱 앱 정보</h3>
                    <SettingsMenuItem title="오픈소스 라이선스" onClick={() => window.open("/agreement/opensource.html", "_blank")} />
                </div>

                {/* 로그아웃 */}
                {userInfo !== null && (
                    <div className="settings-logout-section">
                        <button
                            className="settings-logout-btn"
                            onClick={() => setShowLogoutModal(true)}
                        >
                            로그아웃
                        </button>
                        <button
                            className="settings-withdraw-link"
                            onClick={() => setShowWithdrawModal(true)}
                        >
                            회원탈퇴
                        </button>
                    </div>
                )}
            </div>

            <CommonModal
                isOpen={showLogoutModal}
                onRequestClose={() => setShowLogoutModal(false)}
                className="logout-modal"
            >
                <>
                    <div className="logout-modal-title">로그아웃 하시겠습니까?</div>
                    <button className="btn btn-login" onClick={onClickLogout}>
                        확인
                    </button>
                </>
            </CommonModal>

            <CommonModal
                isOpen={showWithdrawModal}
                onRequestClose={() => setShowWithdrawModal(false)}
                className="logout-modal"
            >
                <>
                    <div className="logout-modal-title">회원탈퇴 하시겠습니까?</div>
                    <button
                        className="btn btn-login"
                        onClick={() => {
                            setShowWithdrawModal(false);
                            navigate("/withdraw");
                        }}
                    >
                        확인
                    </button>
                </>
            </CommonModal>
        </div>
    );
};

export default Settings;
